/*
 * SearchGuided: the header search panel. It asks the gift finder's questions as tap rows in the
 * quiz's words, order and person rules, and shows the gift the quiz would open for the answers
 * (the "pick"). Taps land on the collection heroes and quiz gifts in assets/search-guided-data.js
 * (scripts/build-search-data.mjs, loaded on first open). Typed product results come from Prefixbox
 * via window.SearchEngine so the whole live catalog stays searchable; the words are still parsed
 * onto quiz answers for the pills, the pick, collections, help answers and the redeem door.
 *
 * Ranges, filters and the pick use the snapshot's USD ladder prices, so the same answers open the
 * same products as the quiz in every market. Displayed prices come from /products/<handle>.js in
 * the visitor's currency; that read also enforces the quiz's open rule (available, first image).
 *
 * theme.js (Search) owns opening, closing, focus and the field; it calls show() on every input.
 */
(function () {
  'use strict';

  let D = null;
  let dataPromise = null;
  let CFG = null;
  let S = null;

  const states = new Map();
  const live = new Map();
  const livePending = new Set();

  const PERSON = [
    'partner',
    'couple',
    'mom',
    'dad',
    'parents',
    'grandparent',
    'child',
    'teen',
    'family',
    'friend',
  ];
  const ALLOW = {
    partner: ['partner', 'couple', 'her', 'him'],
    mom: ['mom', 'her'],
    dad: ['dad', 'him'],
    parents: ['parents', 'couple'],
    grandparent: ['grandparent'],
    friend: ['friend', 'her', 'him'],
    couple: ['couple', 'partner'],
    family: ['family', 'child'],
    child: ['child', 'family'],
    teen: ['teen', 'child'],
    colleague: ['her', 'him'],
    team: [],
    teacher: ['her', 'him'],
    other: ['her', 'him'],
  };
  const OCCX = {
    holiday: ['holiday', 'christmas'],
    christmas: ['christmas', 'holiday'],
    thanks: ['thanks'],
    new_year: ['new_year', 'holiday'],
  };
  const WORK = [
    'last-minute-experience-gift-thank-you-for-your-service',
    'christmas-experience-gift-secret-santa',
    'affordable-experience-gift-box',
    'food-and-drink-experience-gift-box',
    'experience-gift-card',
    'christmas-experience-gift-happy-holidays',
  ];
  // Valentine's follows Christmas from Dec 26, as the navigation's seasonal card does.
  const SEASON = {
    '4th-of-july-gifts': ['06-01', '07-04'],
    'valentines-day-experience-gifts': ['12-26', '02-14'],
  };
  const ANCHOR = { b1: 99, b2: 149, b3: 249, b4: 399, b5: 599 };
  const OCCASION_COL = {
    birthday: 'birthday',
    christmas: 'christmas',
    thanksgiving: 'thanksgiving',
    wedding: 'wedding',
    anniversary: 'couples',
  };
  const EXPERIENCE_COL = {
    bucketlist: 'bucketlist',
    travel: 'travel',
    adventure: 'adventure',
    food: 'food',
    spa: 'spa',
    cruise: 'cruise',
    flying: 'flying',
    tours: 'tours',
    attractions: 'attractions',
    entertainment: 'entertainment',
    driving: 'driving',
    cooking: 'cooking',
    arts: 'arts',
  };
  const RECENT_KEY = 'everwow_recent_searches';
  const RECENT_SHOWN = 4;
  const TYPED_DEBOUNCE = 300;
  const TYPED_PAGE = 24;

  /* ------------------------------------------------------------ icons */

  const ICON = {
    back: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15 5l-7 7 7 7"/></svg>',
    mag: '<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="10.5" cy="10.5" r="6"/><path d="M15 15l5 5"/></svg>',
    clock:
      '<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12.5" r="8"/><path d="M12 8.3v4.4l2.9 1.8"/></svg>',
    pin: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 21s-6.5-6.1-6.5-11a6.5 6.5 0 0 1 13 0c0 4.9-6.5 11-6.5 11z"/><circle cx="12" cy="10" r="2.3"/></svg>',
    arrow:
      '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 17L17 7M9 7h8v8"/></svg>',
    x: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 7l10 10M17 7L7 17"/></svg>',
    sparkle:
      '<svg viewBox="0 0 22 22" aria-hidden="true"><path d="M6.80176 14.7295L9.68555 16.6875L6.80176 18.6465L4.84277 21.5303L2.88477 18.6465L0 16.6875L2.88477 14.7295L4.84277 11.8447L6.80176 14.7295ZM15.5332 5.99805L21.5312 8.2168L15.5332 10.4365L13.3135 16.4346L11.0947 10.4365L5.09668 8.2168L11.0947 5.99805L13.3135 0L15.5332 5.99805Z" fill="currentColor"/></svg>',
    redeem:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3.5" y="8" width="17" height="4" rx="1"/><path d="M5 12v8.5h14V12M12 8v12.5M12 8c-1.6-3.3-5.2-3.8-5.2-1.3C6.8 8 12 8 12 8zm0 0c1.6-3.3 5.2-3.8 5.2-1.3C17.2 8 12 8 12 8z"/></svg>',
    box: '<svg viewBox="0 0 64 64" aria-hidden="true"><rect x="10" y="26" width="44" height="30" rx="3" fill="currentColor" opacity=".55"/><rect x="7" y="18" width="50" height="10" rx="2" fill="currentColor"/></svg>',
  };

  /* ------------------------------------------------------------ helpers */

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  // Config strings come from Liquid's `t`, which has already HTML-escaped them.
  function str(key, tokens) {
    let s = (CFG.strings && CFG.strings[key]) || '';
    Object.keys(tokens || {}).forEach(function (k) {
      s = s.split('__' + k.toUpperCase() + '__').join(tokens[k]);
    });
    return s;
  }

  function routeRoot() {
    return (
      (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ||
      '/'
    );
  }

  function productUrl(handle, variantId) {
    return (
      routeRoot() +
      'products/' +
      encodeURIComponent(handle) +
      (variantId ? '?variant=' + variantId : '')
    );
  }

  function collectionUrl(handle) {
    return routeRoot() + 'collections/' + encodeURIComponent(handle);
  }

  function searchUrl(q) {
    return routeRoot() + 'search?q=' + encodeURIComponent(q) + '&type=product';
  }

  function today() {
    const d = new Date();
    const pad = function (n) {
      return String(n).padStart(2, '0');
    };
    return (
      d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate())
    );
  }

  function track(event, params) {
    if (window.EwTrack)
      window.EwTrack.push(
        event,
        Object.assign(
          { device: window.EwTrack.device && window.EwTrack.device() },
          params,
        ),
      );
  }

  function money(amount) {
    if (window.SearchEngine) return window.SearchEngine.formatMoney(amount);
    return '$' + Number(amount).toFixed(2);
  }

  function isUsd() {
    return (
      !window.theme ||
      !window.theme.currencyCode ||
      window.theme.currencyCode === 'USD'
    );
  }

  function sizedImage(url, width) {
    if (!url) return '';
    const abs = url.indexOf('//') === 0 ? 'https:' + url : url;
    try {
      const u = new URL(abs, window.location.origin);
      u.searchParams.set('width', String(width));
      return u.href;
    } catch (e) {
      return abs;
    }
  }

  /* ------------------------------------------------------------ data */

  function loadConfig() {
    if (CFG) return CFG;
    const el = document.querySelector('[data-search-guided-config]');
    try {
      CFG = el ? JSON.parse(el.textContent) : { strings: {} };
    } catch (e) {
      CFG = { strings: {} };
    }
    return CFG;
  }

  function loadData() {
    if (window.EW_SEARCH_DATA) {
      D = window.EW_SEARCH_DATA;
      return Promise.resolve(D);
    }
    if (dataPromise) return dataPromise;
    dataPromise = new Promise(function (resolve, reject) {
      const src = loadConfig().dataUrl;
      if (!src) return reject(new Error('SearchGuided: no dataUrl'));
      const script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.onload = function () {
        D = window.EW_SEARCH_DATA || null;
        if (D) {
          prepare();
          resolve(D);
        } else
          reject(
            new Error(
              'SearchGuided: data script loaded without EW_SEARCH_DATA',
            ),
          );
      };
      script.onerror = function () {
        dataPromise = null;
        reject(new Error('SearchGuided: data script failed'));
      };
      document.head.appendChild(script);
    });
    return dataPromise;
  }

  let HIDX = {};
  let PLANNED = {};
  let SNAPIMG = {};
  let VOCAB = {};

  function prepare() {
    HIDX = {};
    D.prods.forEach(function (p) {
      HIDX[p[1]] = p;
    });
    PLANNED = {};
    (D.planned || []).forEach(function (h) {
      PLANNED[h] = 1;
    });
    SNAPIMG = {};
    D.prods.forEach(function (p) {
      SNAPIMG[p[1]] = p[2] || '';
    });
    D.named.forEach(function (n) {
      n[3].forEach(function (x) {
        if (!(x[1] in SNAPIMG)) SNAPIMG[x[1]] = x[2] || '';
      });
    });
    VOCAB = {};
    D.vocab.forEach(function (w) {
      VOCAB[w] = 1;
    });
    ['rec', 'occ', 'exp', 'her', 'him', 'help', 'coll'].forEach(function (k) {
      Object.keys(D.syn[k] || {}).forEach(function (id) {
        D.syn[k][id].forEach(function (ph) {
          ph.split(' ').forEach(function (w) {
            if (w.length > 3) VOCAB[w] = 1;
          });
        });
      });
    });
    (D.places || []).forEach(function (p) {
      norm(p[0])
        .split(' ')
        .forEach(function (w) {
          if (w.length > 3) VOCAB[w] = 1;
        });
    });
  }

  /* ------------------------------------------------------------ live store read (open rule + price) */

  function usableH(h) {
    const a = live.get(h);
    if (a) return !!a.ok;
    return !!SNAPIMG[h] && !PLANNED[h];
  }

  function usable(p) {
    return usableH(p[1]);
  }

  function liveCheck(h) {
    if (typeof fetch !== 'function' || live.has(h) || livePending.has(h))
      return;
    livePending.add(h);
    const ctl =
      typeof AbortController === 'function' ? new AbortController() : null;
    const timer = setTimeout(function () {
      if (ctl) ctl.abort();
    }, 2500);
    fetch(routeRoot() + 'products/' + encodeURIComponent(h) + '.js', {
      credentials: 'same-origin',
      signal: ctl ? ctl.signal : undefined,
    })
      .then(function (r) {
        if (r.status === 404) return null;
        if (!r.ok) throw new Error('store answered ' + r.status);
        return r.json();
      })
      .then(function (j) {
        const was = usableH(h);
        if (!j) {
          live.set(h, { ok: false, variants: [] });
        } else {
          const image = j.featured_image || (j.images && j.images[0]) || '';
          live.set(h, {
            ok: !!image && j.available !== false,
            image: image,
            variants: (j.variants || []).map(function (v) {
              return {
                id: v.id,
                title: v.title,
                price: v.price / 100,
                available: v.available,
              };
            }),
          });
        }
        if (was !== usableH(h) || !isUsd()) renderAll();
        else renderAll(true);
      })
      .catch(function () {})
      .then(function () {
        clearTimeout(timer);
        livePending.delete(h);
      });
  }

  function liveVariant(handle, label) {
    const L = live.get(handle);
    if (!L || !L.variants || !L.variants.length) return null;
    if (label == null) return L.variants[0];
    return (
      L.variants.find(function (v) {
        return v.title === label;
      }) || null
    );
  }

  /* ------------------------------------------------------------ the quiz's rules, ported line by line */

  function phaseOf(d) {
    if (d >= '2026-12-26' && d <= '2026-12-31') return 2;
    if (d >= '2026-11-01' && d <= '2026-12-25') return 1;
    return 0;
  }

  function level(mx, key, r) {
    return (mx[key] || {})[r] || '-';
  }

  function occScore(r, o, ph) {
    const oc = D.occ[o];
    if (!r) return oc.m[ph] * (D.G.occ[o] || 0);
    const L = level(D.om, o, r) === 'C' ? 1 : 0.45;
    const w = D.W[r] || 0;
    const g = D.G.occ[o] || 0;
    const c = (D.C.occ[r] || {})[o];
    const base = c === undefined || r === 'other' ? g : (1 - w) * g + w * c;
    return oc.m[ph] * base * L;
  }

  function occOptions(r, d) {
    const ph = phaseOf(d);
    const out = [];
    Object.keys(D.occ).forEach(function (o) {
      const oc = D.occ[o];
      if (r && level(D.om, o, r) === '-') return;
      if (oc.u && d > oc.u && d >= '2026-09-01' && d <= '2026-12-31') return;
      if (oc.m[ph] === 0 && d >= '2026-09-01' && d <= '2026-12-31') return;
      out.push([o, occScore(r, o, ph) - (oc.f ? 1e6 : 0)]);
    });
    out.sort(function (a, b) {
      return b[1] - a[1];
    });
    return out.map(function (x) {
      return x[0];
    });
  }

  function expScore(r, e) {
    if (!r) return D.G.exp[e] || 0;
    const L = level(D.em, e, r) === 'C' ? 1 : 0.45;
    const w = D.W[r] || 0;
    const g = D.G.exp[e] || 0;
    const c = ((D.C.exp || {})[r] || {})[e];
    return (c === undefined || r === 'other' ? g : (1 - w) * g + w * c) * L;
  }

  function expOptions(r) {
    return Object.keys(D.exp)
      .filter(function (e) {
        return e !== 'choice' && (!r || level(D.em, e, r) !== '-');
      })
      .sort(function (a, b) {
        return expScore(r, b) - expScore(r, a);
      });
  }

  function occFirst(r, d) {
    let all = occOptions(r, d);
    const keep = D.fkeep.concat(
      r === 'mom' || r === 'dad' || r === 'parents' ? D.fkeepMomDad : [],
    );
    const pin = (D.fkeepFirst || []).filter(function (o) {
      return all.indexOf(o) >= 0;
    });
    all = pin.concat(
      all.filter(function (o) {
        return pin.indexOf(o) < 0;
      }),
    );
    let first = all.filter(function (o) {
      return keep.indexOf(o) >= 0;
    });
    if (first.length < 6)
      all.forEach(function (o) {
        if (first.length < 6 && first.indexOf(o) < 0 && !D.occ[o].f)
          first.push(o);
      });
    let rest = all.filter(function (o) {
      return first.indexOf(o) < 0;
    });
    if (rest.length < 5) {
      first = first.concat(rest);
      rest = [];
    }
    return [first, rest];
  }

  function expFirst(r) {
    const all = expOptions(r);
    let first = all.slice(0, 6);
    let rest = all.slice(6);
    if (rest.length < 5) {
      first = all;
      rest = [];
    }
    return [first, rest];
  }

  function wantPeople(r, part) {
    if (r === 'partner') return { one: [1], pair: [2] }[part] || [2, 1];
    if (r === 'couple' || r === 'parents') return [2];
    if (r === 'family') return [4, 3, 6, 5];
    if (r === 'child' || r === 'teen') return [2, 1];
    return [1];
  }

  function minorsRule(r) {
    if (r === 'child' || r === 'family') return 'child';
    if (r === 'teen') return 'teen';
    return null;
  }

  function recFit(p) {
    const r = S.rec;
    if (!r) return 1;
    const tags = p[4].filter(function (t) {
      return PERSON.indexOf(t) >= 0;
    });
    let allow = ALLOW[r];
    if (r === 'partner' && S.part === 'one') allow = ['partner', 'her', 'him'];
    if (r === 'partner' && S.part === 'pair') allow = ['partner', 'couple'];
    const m = minorsRule(r);
    if (m === 'child' && p[7] !== 'child') return 0;
    if (m === 'teen' && p[7] !== 'child' && p[7] !== 'teen') return 0;
    if (!tags.length) return 1;
    return tags.some(function (t) {
      return allow.indexOf(t) >= 0;
    })
      ? 2
      : 0;
  }

  function occFit(p, o) {
    if (!o) return 1;
    if (!p[5].length) return 1;
    const want = OCCX[o] || [o];
    return p[5].some(function (x) {
      return want.indexOf(x) >= 0;
    })
      ? 2
      : 0;
  }

  function expFit(p, e) {
    if (!e) return 1;
    return p[6].indexOf(e) >= 0 ? 2 : 0;
  }

  function variant(p, band, want) {
    let vs = p[3].filter(function (v) {
      return v[2] == null || want.indexOf(v[2]) >= 0;
    });
    if (!vs.length) vs = p[3];
    if (band)
      vs = vs.filter(function (v) {
        return v[1] >= band.lo && v[1] <= band.hi;
      });
    if (!vs.length) return null;
    vs = vs.slice().sort(function (a, b) {
      return a[1] - b[1];
    });
    return vs[0];
  }

  function offSeason(p) {
    const w = SEASON[p[12]];
    if (!w) return false;
    const md = S.date.slice(5);
    return w[0] <= w[1]
      ? !(md >= w[0] && md <= w[1])
      : !(md >= w[0] || md <= w[1]);
  }

  // Taps land on the collection heroes only; the pick adds the quiz gifts.
  function heroResults() {
    const want = wantPeople(S.rec, S.part);
    const band = S.band;
    const out = [];
    D.prods.forEach(function (p, i) {
      if (p[10] === 'quizgift' || !p[11] || offSeason(p) || !usable(p)) return;
      const rf = recFit(p);
      if (!rf) return;
      const of = occFit(p, S.occ);
      if (!of) return;
      const ef = expFit(p, S.exp);
      if (!ef) return;
      const v = variant(p, band, want);
      if (!v) return;
      const workRec =
        S.rec === 'colleague' || S.rec === 'team' || S.rec === 'teacher';
      const work = workRec && WORK.indexOf(p[1]) >= 0 ? 1 : 0;
      const cityOk = !p[8] ? 1 : 0;
      const amountOk = p[10] !== 'amount' || workRec ? 1 : 0;
      const sold = p[9] > 0 ? 1 : 0;
      out.push({
        p: p,
        v: v,
        k: [of, cityOk, amountOk, work, sold, rf, ef, -i],
      });
    });
    out.sort(function (a, b) {
      for (let j = 0; j < 8; j++) {
        if (a.k[j] !== b.k[j]) return b.k[j] - a.k[j];
      }
      return 0;
    });
    // One row per gift name; among tiers that fit equally, the entry price stands for it.
    const seen = {};
    const res = [];
    out.forEach(function (x) {
      const j = seen[x.p[0]];
      if (j === undefined) {
        seen[x.p[0]] = res.length;
        res.push(x);
        return;
      }
      const y = res[j];
      let same = true;
      for (let q = 0; q < 7; q++) {
        if (x.k[q] !== y.k[q]) {
          same = false;
          break;
        }
      }
      if (same && x.v[1] < y.v[1]) res[j] = x;
    });
    return res;
  }

  function namedGroup() {
    if (!S.rec || !S.occ) return null;
    let r = S.rec;
    if (r === 'partner')
      r = S.part === 'pair' ? 'couple' : S.part === 'one' ? 'partner' : null;
    if (!r) return null;
    return (
      D.named.filter(function (n) {
        return n[1] === S.occ && n[2] === r && !n[4];
      })[0] || null
    );
  }

  function namedItemU(g) {
    const ps = g[3].slice().sort(function (a, b) {
      return a[0] - b[0];
    });
    if (!S.band) {
      const ok = ps.filter(function (x) {
        return usableH(x[1]);
      });
      return ok.length ? { t: g[0], p: ok, all: ok, kind: 'named' } : null;
    }
    const inb = ps.filter(function (x) {
      return x[0] >= S.band.lo && x[0] <= S.band.hi;
    });
    const pr = inb.length ? inb[0] : ps[ps.length - 1];
    return usableH(pr[1])
      ? { t: g[0], p: [pr], all: g[3], kind: 'named' }
      : null;
  }

  // A keep-list experience: the quiz routes it to that collection instead of the quiz gift.
  function namedCovers(g) {
    return !S.exp || S.exp === 'choice' || (g[5] || []).indexOf(S.exp) < 0;
  }

  function priceScore(price, b) {
    const lo = b.lo;
    const anchor = ANCHOR[b.id];
    if (price >= lo && price <= b.hi) return 8 - Math.abs(price - anchor) / 100;
    if (price < lo) return -(lo - price) / 5;
    return -100 - (price - b.hi) / 10;
  }

  function heroRecOk(p) {
    const r = S.rec;
    if (!p[4].length) return true;
    if (!r)
      return !p[4].some(function (t) {
        return PERSON.indexOf(t) >= 0;
      });
    if (r === 'other') return false;
    const tags = p[4];
    if (r === 'teen') return tags.indexOf('teen') >= 0;
    if (r === 'partner') {
      if (S.part === 'one') return false;
      return tags.indexOf('partner') >= 0;
    }
    if (r === 'child') return tags.indexOf('child') >= 0;
    if (r === 'family') return tags.indexOf('family') >= 0;
    return tags.indexOf(r) >= 0;
  }

  function ladder() {
    const L = [];
    const r = S.rec;
    const minor = r === 'child' || r === 'teen' || r === 'family';
    const rung = function (kind, col, bonus) {
      if (D.heroCol[col])
        L.push({ kind: kind, handles: D.heroCol[col], bonus: bonus });
    };
    if (S.occ && OCCASION_COL[S.occ]) rung('occasion', OCCASION_COL[S.occ], 30);
    if (minor) {
      if (r === 'child') rung('recipient', 'kids', 28);
      else if (r === 'teen') rung('recipient', 'adventure', 28);
      else rung('recipient', 'family', 28);
      rung('generic', 'bucketlist', 0);
      return L;
    }
    if (S.exp && EXPERIENCE_COL[S.exp])
      rung('experience', EXPERIENCE_COL[S.exp], 30);
    if (r === 'mom') rung('recipient', 'mom', 20);
    if (r === 'dad') rung('recipient', 'dad', 20);
    if (r === 'grandparent') rung('recipient', 'grandparent', 20);
    if (r === 'parents') rung('recipient', 'parents', 20);
    if (r === 'couple' || (r === 'partner' && S.part !== 'one'))
      rung('recipient', 'couples', 20);
    rung('generic', 'generic', 0);
    return L;
  }

  function heroPick(kinds) {
    let best = null;
    const b = S.band;
    ladder().forEach(function (step, si) {
      if (kinds && kinds.indexOf(step.kind) < 0) return;
      // No range chosen yet: the collection's own three tiers, cheapest first ("From").
      const hs = b
        ? step.handles
        : step.handles
            .filter(function (h) {
              const p = HIDX[h];
              return p && p[10] !== 'quizgift';
            })
            .slice(0, 3);
      hs.forEach(function (h) {
        const p = HIDX[h];
        if (!p || !heroRecOk(p)) return;
        const v = p[3][0];
        // A gap-fill gift: its occasion only, only once a range is chosen, never a range above its price.
        if (p[13] && p[13].indexOf(S.occ) < 0) return;
        if (p[10] === 'quizgift' && (!b || v[1] < b.lo)) return;
        if (!usable(p)) return;
        const sc =
          Math.round(
            (step.bonus + (b ? priceScore(v[1], b) : -v[1] / 1000)) * 100,
          ) / 100;
        if (
          !best ||
          sc > best.sc ||
          (sc === best.sc &&
            (si < best.si || (si === best.si && v[1] < best.v[1])))
        )
          best = {
            p: p,
            v: v,
            sc: sc,
            si: si,
            kind: step.kind,
            over: !!b && v[1] > b.hi,
          };
      });
    });
    return best;
  }

  function finderCore() {
    const g = namedGroup();
    const gi = g ? namedItemU(g) : null;
    if (gi && namedCovers(g)) return gi;
    const hero = heroPick(gi ? ['experience'] : null);
    if (hero && !gi) return { t: hero.p[0], hero: hero, kind: 'hero' };
    if (gi) {
      if (hero && hero.kind === 'experience' && !hero.over)
        return { t: hero.p[0], hero: hero, kind: 'hero' };
      return gi;
    }
    return hero ? { t: hero.p[0], hero: hero, kind: 'hero' } : null;
  }

  function pickPrice(r) {
    return r.kind === 'hero' ? r.hero.v[1] : r.p[0][0];
  }

  function pickHandle(r) {
    return r.kind === 'hero' ? r.hero.p[1] : r.p[0][1];
  }

  function finderPick() {
    if (!S.rec && !S.occ && !S.exp) return null;
    const r = finderCore();
    if (!r || !S.band) return r;
    const price = pickPrice(r);
    if (price <= S.band.hi) return r;
    // Nothing usable fits the range: the same answers one range up, when that is the smaller step.
    let bi = -1;
    D.bud.forEach(function (b, i) {
      if (b.id === S.band.id) bi = i;
    });
    const nb = D.bud[bi + 1];
    if (!nb) return r;
    const keep = S.band;
    S.band = nb;
    let r2 = null;
    try {
      r2 = finderCore();
    } finally {
      S.band = keep;
    }
    if (r2 && pickPrice(r2) <= price) {
      r2.stepUp = nb.id;
      return r2;
    }
    return r;
  }

  /* ------------------------------------------------------------ typed words to quiz answers */

  function norm(s) {
    return String(s)
      .toLowerCase()
      .replace(/[’']/g, '')
      .replace(/&/g, ' and ')
      .replace(/[^a-z0-9$ ]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function lev(a, b) {
    if (Math.abs(a.length - b.length) > 1) return 9;
    const m = a.length;
    const n = b.length;
    const d = [];
    for (let i = 0; i <= m; i++) d[i] = [i];
    for (let j = 0; j <= n; j++) d[0][j] = j;
    for (let i = 1; i <= m; i++)
      for (let j = 1; j <= n; j++)
        d[i][j] = Math.min(
          d[i - 1][j] + 1,
          d[i][j - 1] + 1,
          d[i - 1][j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1),
        );
    return d[m][n];
  }

  function fixTypos(q) {
    let fixed = false;
    const out = q
      .split(' ')
      .map(function (w) {
        if (w.length < 4 || VOCAB[w] || /\d/.test(w)) return w;
        let best = null;
        Object.keys(VOCAB).some(function (v) {
          if (v[0] === w[0] && lev(w, v) <= 1) {
            best = v;
            return true;
          }
          return false;
        });
        if (best) {
          fixed = true;
          return best;
        }
        return w;
      })
      .join(' ');
    return { q: out, fixed: fixed };
  }

  function findPhrase(q, map) {
    let hit = null;
    let len = 0;
    Object.keys(map).forEach(function (id) {
      map[id].forEach(function (ph) {
        const n = norm(ph);
        if ((' ' + q + ' ').indexOf(' ' + n + ' ') >= 0 && n.length > len) {
          hit = id;
          len = n.length;
        }
      });
    });
    return hit ? { id: hit, len: len } : null;
  }

  function stripPhrases(q, maps) {
    const all = [];
    maps.forEach(function (map) {
      Object.keys(map).forEach(function (id) {
        map[id].forEach(function (ph) {
          all.push(norm(ph));
        });
      });
    });
    all.sort(function (a, b) {
      return b.length - a.length;
    });
    all.forEach(function (n) {
      q = (' ' + q + ' ')
        .split(' ' + n + ' ')
        .join(' ')
        .replace(/\s+/g, ' ')
        .trim();
    });
    return q;
  }

  function parse(raw) {
    const q0 = norm(raw);
    if (!q0) return null;
    const ty = fixTypos(q0);
    const q = ty.q;
    const SYN = D.syn;
    const r = { q0: q0, q: q, fixed: ty.fixed && q !== q0 };
    if (
      /\b(redeem|claim|gift code|my code|received a gift|got a gift|activate)\b/.test(
        q,
      )
    )
      r.redeem = true;
    const h = findPhrase(q, SYN.help || {});
    if (h) r.help = h.id;
    const rc = findPhrase(q, SYN.rec || {});
    if (rc) r.rec = rc.id;
    const oc = findPhrase(q, SYN.occ || {});
    if (oc) r.occ = oc.id;
    const ex = findPhrase(q, SYN.exp || {});
    if (ex) r.exp = ex.id;
    const cl = findPhrase(q, SYN.coll || {});
    if (cl) r.coll = cl.id;
    if (findPhrase(q, SYN.her || {})) r.her = 'her';
    if (findPhrase(q, SYN.him || {})) r.her = 'him';
    (D.places || []).forEach(function (pl) {
      if (!r.place && (' ' + q + ' ').indexOf(' ' + norm(pl[0]) + ' ') >= 0)
        r.place = pl;
    });
    let m;
    if ((m = q.match(/\$?(\d{2,5}) ?(?:to|and|-) ?\$?(\d{2,5})/)))
      r.budget = { lo: +m[1], hi: +m[2] };
    else if (
      (m = q.match(
        /(?:under|below|less than|up to|max|within|no more than) ?\$?(\d{2,5})/,
      ))
    )
      r.budget = { lo: 0, hi: +m[1] };
    else if (
      (m = q.match(/(?:over|above|more than|at least|from) ?\$?(\d{2,5})/))
    )
      r.budget = { lo: +m[1], hi: 100000 };
    else if ((m = q.match(/(?:^| )\$(\d{2,5})(?: |$)/)))
      r.budget = { lo: 0, hi: +m[1] };
    else if (
      /\b(cheap|affordable|budget|inexpensive|small gift|low cost)\b/.test(q)
    )
      r.budget = { lo: 0, hi: 100 };
    else if (
      /\b(luxury|luxe|premium|high end|splurge|expensive|lavish)\b/.test(q)
    )
      r.budget = { lo: 300, hi: 100000 };
    if (r.budget) {
      D.bud.forEach(function (b) {
        if (b.lo === r.budget.lo && b.hi === r.budget.hi) r.budget = b;
      });
    }
    let rest = stripPhrases(q, [
      SYN.rec || {},
      SYN.occ || {},
      SYN.exp || {},
      SYN.help || {},
      SYN.her || {},
      SYN.him || {},
      SYN.coll || {},
    ]);
    if (r.place)
      rest = (' ' + rest + ' ').split(' ' + norm(r.place[0]) + ' ').join(' ');
    r.words = rest.split(' ').filter(function (w) {
      return (
        w.length > 2 && !/^\$?\d+$/.test(w) && (D.stop || []).indexOf(w) < 0
      );
    });
    return r;
  }

  /* ------------------------------------------------------------ rendering */

  function snapImage(p) {
    const L = live.get(p);
    if (L && L.image) return sizedImage(L.image, 160);
    const img = SNAPIMG[p];
    return img
      ? D.cdn + img + (img.indexOf('?') >= 0 ? '&' : '?') + 'width=160'
      : '';
  }

  function thumb(handle) {
    const src = snapImage(handle);
    return src
      ? '<img src="' +
          esc(src) +
          '" alt="" loading="lazy" width="64" height="64">'
      : '<span class="sr-ph">' + ICON.box + '</span>';
  }

  // The price for one snapshot variant: live in the visitor's currency, else the USD snapshot in
  // the US market, else nothing yet (the live read re-renders).
  function priceOf(handle, label, snapshot) {
    const lv = liveVariant(handle, label);
    if (lv) return { text: money(lv.price), id: lv.id };
    if (isUsd() && snapshot != null) return { text: money(snapshot), id: null };
    return { text: '', id: null };
  }

  function amountPrice(p) {
    const L = live.get(p[1]);
    if (L && L.variants && L.variants.length) {
      const min = Math.min.apply(
        null,
        L.variants.map(function (v) {
          return v.price;
        }),
      );
      return money(min);
    }
    return isUsd() ? money(p[3][0][1]) : '';
  }

  function priceLine(x, pr) {
    const p = x.p;
    const v = x.v;
    if (p[10] === 'amount') {
      const a = amountPrice(p);
      return a
        ? '<span class="sr-pr">' +
            str('any_amount', { price: '<b>' + a + '</b>' }) +
            '</span>'
        : '';
    }
    if (!pr.text) return '';
    const amount = '<b>' + pr.text + '</b>';
    const people = v[2] && v[2] > 1 ? str('for_people', { count: v[2] }) : '';
    if (S.band || p[3].length < 2)
      return '<span class="sr-pr">' + amount + people + '</span>';
    return (
      '<span class="sr-pr">' +
      str('from', { price: amount }) +
      people +
      '</span>'
    );
  }

  function prodRow(list, head, n, list_id, noTag) {
    const shown = list.slice(0, n || 4);
    const rows = shown
      .map(function (x, i) {
        const p = x.p;
        liveCheck(p[1]);
        const pr = priceOf(p[1], x.v[0], x.v[1]);
        const title = p[10] === 'amount' ? str('amount') : esc(p[0]);
        const tag =
          !noTag && p[9] >= 5
            ? '<i class="sr-tag">' + str('most_gifted') + '</i>'
            : '';
        return (
          '<a class="sr-p" href="' +
          esc(productUrl(p[1], pr.id)) +
          '" data-track-result="' +
          esc(p[1]) +
          '" data-track-list="' +
          list_id +
          '" data-track-pos="' +
          (i + 1) +
          '">' +
          thumb(p[1]) +
          '<span><b>' +
          title +
          '</b>' +
          priceLine(x, pr) +
          tag +
          '</span></a>'
        );
      })
      .join('');
    return block(head, '<div class="sr-prods">' + rows + '</div>');
  }

  function chip(label, attrs, icon, cls) {
    return (
      '<button type="button" class="sr-chip' +
      (cls ? ' ' + cls : '') +
      '" ' +
      attrs +
      '>' +
      (icon || '') +
      label +
      '</button>'
    );
  }

  function block(head, inner, extra) {
    return (
      '<div class="sr-blk"><p class="sr-h">' +
      head +
      (extra || '') +
      '</p>' +
      inner +
      '</div>'
    );
  }

  function recPhrase(r) {
    return esc((D.recPhrase || {})[r] || 'them');
  }

  function occPhrase(o) {
    return esc((D.occPhrase || {})[o] || (D.occ[o] ? D.occ[o].l : ''));
  }

  function whoRow(compact) {
    const dr0 = S.door
      ? D.doors.filter(function (d) {
          return d.id === S.door;
        })[0]
      : null;
    if (dr0 && !dr0.sub) {
      S.rec = S.rec || dr0.rec;
      S.door = null;
    }
    let h;
    if (S.door && !S.rec) {
      h =
        chip(str('all'), 'data-door=""', ICON.back, 'sr-backchip') +
        dr0.sub
          .map(function (r) {
            return chip(esc(D.rec[r]), 'data-rec="' + r + '"');
          })
          .join('');
      return block(
        esc(D.copy.recipient),
        '<div class="sr-chips' + (compact ? ' one' : '') + '">' + h + '</div>',
      );
    }
    h = D.doors
      .map(function (d) {
        return d.rec
          ? chip(esc(d.label), 'data-rec="' + d.rec + '"')
          : chip(esc(d.label), 'data-door="' + d.id + '"');
      })
      .join('');
    return block(
      esc(D.copy.prompt),
      '<div class="sr-chips' + (compact ? ' one' : '') + '">' + h + '</div>',
    );
  }

  function partRow() {
    return block(
      esc(D.copy.participation),
      '<div class="sr-chips">' +
        D.part
          .map(function (x) {
            return chip(esc(x.label), 'data-part="' + x.id + '"');
          })
          .join('') +
        '</div>',
    );
  }

  function occRow(compact) {
    const fr = occFirst(S.rec, S.date);
    const open = S.more.occ;
    let h = fr[0]
      .concat(open ? fr[1] : [])
      .map(function (o) {
        return chip(esc(D.occ[o].l), 'data-occ="' + o + '"');
      })
      .join('');
    if (fr[1].length && !open)
      h +=
        '<button type="button" class="sr-more" data-more="occ">' +
        esc(D.copy.moreOccasions) +
        '</button>';
    return block(
      esc(D.copy.occasion),
      '<div class="sr-chips' +
        (compact && !open ? ' one' : '') +
        '">' +
        h +
        '</div>',
    );
  }

  function expRow(compact) {
    const fr = expFirst(S.rec);
    const open = S.more.exp;
    let h = fr[0]
      .concat(open ? fr[1] : [])
      .map(function (e) {
        return chip(esc(D.exp[e].l), 'data-exp="' + e + '"');
      })
      .join('');
    if (fr[1].length && !open)
      h +=
        '<button type="button" class="sr-more" data-more="exp">' +
        esc(D.copy.moreExperiences) +
        '</button>';
    return block(
      esc(D.copy.experience),
      '<div class="sr-chips' +
        (compact && !open ? ' one' : '') +
        '">' +
        h +
        '</div>',
    );
  }

  function budRow(compact) {
    const h =
      D.bud
        .map(function (b) {
          return chip(esc(b.l), 'data-band="' + b.id + '"');
        })
        .join('') + chip(str('amount'), 'data-amount="1"', '', 'sr-amt');
    return block(
      esc(D.copy.budget),
      '<div class="sr-chips' + (compact ? ' one' : '') + '">' + h + '</div>',
    );
  }

  function pills() {
    const L = [];
    if (S.rec)
      L.push([
        'rec',
        esc(D.rec[S.rec]) +
          (S.rec === 'partner' && S.part
            ? S.part === 'pair'
              ? str('partner_pair')
              : str('partner_one')
            : ''),
      ]);
    if (S.occ) L.push(['occ', esc(D.occ[S.occ].l)]);
    if (S.exp) L.push(['exp', esc(D.exp[S.exp].l)]);
    if (S.band)
      L.push([
        'band',
        esc(
          S.band.l ||
            (S.band.lo ? 'From $' + S.band.lo : 'Up to $' + S.band.hi),
        ),
      ]);
    if (!L.length) return '';
    return (
      '<div class="sr-pills" role="group" aria-label="' +
      str('chosen') +
      '">' +
      L.map(function (x) {
        return (
          '<span class="sr-pill">' +
          x[1] +
          '<button type="button" data-drop="' +
          x[0] +
          '" aria-label="' +
          str('remove', { label: x[1] }) +
          '">' +
          ICON.x +
          '</button></span>'
        );
      }).join('') +
      '<button type="button" class="sr-clearall" data-drop="all">' +
      str('clear_all') +
      '</button></div>'
    );
  }

  function quizCard() {
    if (!CFG.quizUrl) return '';
    return (
      '<a class="sr-quiz" href="' +
      esc(CFG.quizUrl) +
      '" data-track-quiz>' +
      ICON.sparkle +
      '<span><b>' +
      str('quiz_title') +
      '</b>' +
      str('quiz_sub') +
      '</span><i>' +
      str('quiz_badge') +
      '</i></a>'
    );
  }

  function redeem(top) {
    if (!CFG.redeemUrl) return '';
    return (
      '<a class="sr-redeem' +
      (top ? ' top' : '') +
      '" href="' +
      esc(CFG.redeemUrl) +
      '">' +
      ICON.redeem +
      '<span>' +
      str('redeem') +
      '</span></a>'
    );
  }

  function mostGifted() {
    const L = (D.best || [])
      .map(function (h) {
        const p = HIDX[h];
        return p && usable(p)
          ? {
              p: p,
              v: p[3].slice().sort(function (a, b) {
                return a[1] - b[1];
              })[0],
            }
          : null;
      })
      .filter(Boolean);
    return L.length
      ? prodRow(L, str('most_gifted'), 3, 'most_gifted', true)
      : '';
  }

  function pickCard(nm) {
    let handle;
    let title;
    let pr;
    let from = '';
    if (nm.kind === 'hero') {
      const hp = nm.hero.p;
      const hv = nm.hero.v;
      handle = hp[1];
      title = hp[0];
      pr = priceOf(handle, hv[0], hv[1]);
      from = S.band ? '' : 'from';
    } else {
      const it = nm.p[0];
      handle = it[1];
      title = nm.t;
      pr = priceOf(handle, null, it[0]);
      from = nm.all.length > 1 && !S.band ? 'from' : '';
    }
    liveCheck(handle);
    const amount = pr.text ? '<b>' + pr.text + '</b>' : '';
    const price = amount
      ? '<span class="sr-pr">' +
        (from ? str('from', { price: amount }) : amount) +
        '</span>'
      : '';
    return block(
      str('pick_heading'),
      '<a class="sr-pick" href="' +
        esc(productUrl(handle, pr.id)) +
        '" data-track-result="' +
        esc(handle) +
        '" data-track-list="pick" data-track-pos="1">' +
        thumb(handle) +
        '<span><b>' +
        esc(title) +
        '</b>' +
        price +
        '<i>' +
        str('pick_sub') +
        '</i></span></a>',
    );
  }

  function resHead() {
    const o = S.occ ? occPhrase(S.occ) : '';
    const r = S.rec ? recPhrase(S.rec) : '';
    if (o && r) return str('occasion_for', { occasion: o, who: r });
    if (o) return o;
    if (r) return str('gifts_for', { who: r });
    if (S.exp) return esc(D.exp[S.exp].l);
    if (S.band)
      return str('gifts_in_range', {
        range: esc(S.band.l.replace(/^Up/, 'up').replace(/^\$/, 'from $')),
      });
    return str('gifts');
  }

  function collRows(list) {
    return block(
      str('collections'),
      '<div class="sr-cl">' +
        list
          .map(function (c) {
            const n = D.collN[c[1]];
            return (
              '<a class="sr-c" href="' +
              esc(collectionUrl(c[1])) +
              '" data-track-coll="' +
              esc(c[1]) +
              '"><span>' +
              esc(c[0]) +
              '</span><i>' +
              (n ? str('gift_count', { count: n }) : '') +
              '</i></a>'
            );
          })
          .join('') +
        '</div>',
    );
  }

  function tapSeeAll(count) {
    let handle = null;
    if (S.occ && D.occColl[S.occ]) handle = D.occColl[S.occ][1];
    else if (S.exp && D.exp[S.exp]) handle = D.exp[S.exp].h;
    else if (S.rec && D.recColl[S.rec]) handle = D.recColl[S.rec][1];
    else handle = 'experience-gifts';
    const n = D.collN[handle];
    const label = n ? str('see_all', { count: n }) : str('see_all_plain');
    return count > 4
      ? '<a class="sr-all" href="' +
          esc(collectionUrl(handle)) +
          '">' +
          label +
          '</a>'
      : '';
  }

  function noResults(soft) {
    let h =
      '<div class="sr-none"><b>' +
      (soft ? str('none_soft_title') : str('none_title', { q: esc(S.q) })) +
      '</b><span>' +
      (soft ? str('none_soft_sub') : str('none_sub')) +
      '</span></div>';
    if (!soft)
      h +=
        quizCard() +
        block(
          str('try'),
          '<div class="sr-chips">' +
            (D.tryChips || [])
              .map(function (c) {
                return chip(esc(c), 'data-q="' + esc(c) + '"', ICON.mag);
              })
              .join('') +
            '</div>',
        ) +
        mostGifted() +
        '<p class="sr-note">' +
        str('help_line') +
        '</p>';
    return h;
  }

  function recentList() {
    try {
      const raw = localStorage.getItem(RECENT_KEY);
      const list = raw ? JSON.parse(raw) : [];
      return Array.isArray(list) ? list.slice(0, RECENT_SHOWN) : [];
    } catch (e) {
      return [];
    }
  }

  function saveRecent(q) {
    const query = String(q || '').trim();
    if (!query) return;
    try {
      const list = recentList().filter(function (s) {
        return s !== query;
      });
      list.unshift(query);
      localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, 5)));
    } catch (e) {
      // Private mode or blocked storage: recent searches are a convenience only.
    }
  }

  function clearRecent() {
    try {
      localStorage.removeItem(RECENT_KEY);
    } catch (e) {
      // As above.
    }
  }

  function emptyView() {
    let A = quizCard();
    const recent = recentList();
    if (recent.length)
      A += block(
        str('recent'),
        '<div class="sr-list">' +
          recent
            .map(function (r) {
              return (
                '<button type="button" class="sr-li" data-q="' +
                esc(r) +
                '">' +
                ICON.clock +
                '<span>' +
                esc(r) +
                '</span></button>'
              );
            })
            .join('') +
          '</div>',
        ' <button type="button" class="sr-clear" data-clear-recent>' +
          str('clear') +
          '</button>',
      );
    // The price row is never on the first screen: it is asked after the person, occasion or experience.
    A += whoRow() + occRow() + expRow();
    const B = mostGifted() + redeem(false);
    return cols(A, B);
  }

  function refineView() {
    let A = pills();
    let B = '';
    if (S.rec === 'partner' && !S.part) A += partRow();
    if (!S.rec) A += whoRow(true);
    if (!S.occ) A += occRow(true);
    if (!S.exp && !S.occ) A += expRow(true);
    if (
      !S.band &&
      (S.occ ||
        S.exp ||
        (S.rec && S.rec !== 'partner') ||
        (S.rec === 'partner' && S.part))
    )
      A += budRow(true);
    if (S.context === 'desktop') A += quizCard();
    const nm = finderPick();
    if (nm) B += pickCard(nm);
    let R = heroResults();
    if (nm && nm.kind === 'hero')
      R = R.filter(function (x) {
        return x.p[1] !== nm.hero.p[1];
      });
    if (R.length) B += prodRow(R, resHead(), 4, 'results');
    B += tapSeeAll(R.length);
    if (!R.length && !nm) B += noResults(true);
    if (S.rec && D.recColl[S.rec]) B += collRows([D.recColl[S.rec]]);
    return cols(A, B);
  }

  function typedRows(r) {
    const T = S.typed;
    if (T.q !== S.q || T.status === 'loading')
      return {
        html: '<p class="sr-loading">' + str('loading') + '</p>',
        count: -1,
      };
    if (T.status === 'error') return { html: '', count: 0 };
    let docs = T.docs;
    const band = r.budget;
    if (band)
      docs = docs.filter(function (d) {
        return (
          d.basePrice != null &&
          d.basePrice >= band.lo &&
          d.basePrice <= band.hi
        );
      });
    if (!docs.length) return { html: '', count: 0 };
    const rows = docs
      .slice(0, 4)
      .map(function (d, i) {
        let price = d.priceText || '';
        if (!price && window.SearchEngine && d.basePrice != null) {
          const pf = window.SearchEngine.priceFor(d.basePrice);
          if (pf) price = pf.price;
        }
        const label =
          price && window.SearchEngine && window.SearchEngine.priceFromLabel(d);
        const priceHtml = price
          ? '<span class="sr-pr">' +
            (label ? esc(label) + ' ' : '') +
            '<b>' +
            price +
            '</b></span>'
          : '';
        const img = d.image
          ? '<img src="' +
            esc(sizedImage(d.image, 160)) +
            '" alt="" loading="lazy" width="64" height="64">'
          : '<span class="sr-ph">' + ICON.box + '</span>';
        return (
          '<a class="sr-p" href="' +
          esc(d.url) +
          '" data-track-result="' +
          esc(d.handle || d.title) +
          '" data-track-list="typed" data-track-pos="' +
          (i + 1) +
          '">' +
          img +
          '<span><b>' +
          esc(d.title) +
          '</b>' +
          priceHtml +
          '</span></a>'
        );
      })
      .join('');
    // The live catalog does not filter by the parsed person or occasion, so the heading must not claim it.
    let html = block(str('gifts'), '<div class="sr-prods">' + rows + '</div>');
    const trust = !band && Number.isFinite(T.total) && !T.sprawl;
    const more = band ? docs.length > 4 : T.total > 4 || T.total === null;
    if (more)
      html +=
        '<a class="sr-all" href="' +
        esc(searchUrl(S.q.trim())) +
        '">' +
        (trust ? str('see_all', { count: T.total }) : str('see_all_plain')) +
        '</a>';
    return { html: html, count: docs.length };
  }

  function typedView() {
    const r = parse(S.q);
    if (!r) return emptyView();
    const saved = {
      rec: S.rec,
      occ: S.occ,
      exp: S.exp,
      band: S.band,
      part: S.part,
    };
    if (r.rec) S.rec = r.rec;
    if (r.occ) S.occ = r.occ;
    if (r.exp) S.exp = r.exp;
    if (r.budget) S.band = r.budget;
    let A = '';
    let B = '';
    let city = null;
    if (r.redeem) A += redeem(true);
    if (r.fixed)
      A += '<p class="sr-fix">' + str('fixed') + ' <b>' + esc(r.q) + '</b></p>';
    if (r.help) {
      const hq = str('help_' + r.help + '_q');
      if (hq)
        A +=
          '<div class="sr-help"><b>' +
          hq +
          '</b><span>' +
          str('help_' + r.help + '_a') +
          '</span></div>';
    }
    if (r.place) {
      const pl = r.place;
      if (pl[1]) {
        A +=
          '<div class="sr-place">' +
          ICON.pin +
          '<span><b>' +
          str('place_yes_title', { place: esc(pl[0]) }) +
          '</b>' +
          str('place_yes') +
          '</span></div>';
        city = pl[1];
      } else
        A +=
          '<div class="sr-place">' +
          ICON.pin +
          '<span><b>' +
          esc(pl[0]) +
          '</b>' +
          str('place_any') +
          '</span></div>';
    }
    if (S.rec || S.occ || S.exp || S.band) A += pills();
    const qn = r.q;
    const max = S.context === 'desktop' ? 8 : 6;
    const sg = [];
    (D.sugg || []).forEach(function (s) {
      if (
        sg.length < max &&
        norm(s).indexOf(qn) === 0 &&
        norm(s) !== qn &&
        sg.indexOf(s) < 0
      )
        sg.push(s);
    });
    if (sg.length < 3)
      (D.sugg || []).forEach(function (s) {
        if (sg.length < max && norm(s).indexOf(qn) > 0 && sg.indexOf(s) < 0)
          sg.push(s);
      });
    if (sg.length)
      A +=
        '<div class="sr-list">' +
        sg
          .map(function (s) {
            const i = norm(s).indexOf(qn);
            const hl =
              i === 0
                ? esc(s.slice(0, qn.length)) +
                  '<b>' +
                  esc(s.slice(qn.length)) +
                  '</b>'
                : '<b>' + esc(s) + '</b>';
            return (
              '<button type="button" class="sr-li" data-q="' +
              esc(s) +
              '" data-track-suggestion>' +
              ICON.mag +
              '<span>' +
              hl +
              '</span><i class="sr-copy">' +
              ICON.arrow +
              '</i></button>'
            );
          })
          .join('') +
        '</div>';
    const colls = [];
    if (S.rec && D.recColl[S.rec]) colls.push(D.recColl[S.rec]);
    if (r.her === 'her')
      colls.push(['Experience Gifts for Women', 'experience-gifts-for-women']);
    if (r.her === 'him')
      colls.push(['Gifts for Him', 'experience-gifts-for-men']);
    if (S.occ && D.occColl[S.occ]) colls.push(D.occColl[S.occ]);
    if (r.coll && D.extraColl[r.coll]) colls.push(D.extraColl[r.coll]);
    if (S.exp && D.exp[S.exp])
      colls.push([
        D.expCollTitle[D.exp[S.exp].h] || D.exp[S.exp].l,
        D.exp[S.exp].h,
      ]);
    if (city) {
      const c = D.cities.filter(function (x) {
        return x[2] === city;
      })[0];
      if (c) colls.push([c[0] + ' Experience Gifts', c[1]]);
    }
    if (colls.length) B += collRows(colls.slice(0, 3));
    const nm = finderPick();
    if (nm) B += pickCard(nm);
    if (r.budget && r.budget.hi < 79)
      B += '<p class="sr-fix">' + str('under_79') + '</p>';
    // A question or a redeem door is the answer; catalog rows under it would be noise.
    const typed = r.help || r.redeem ? { html: '', count: 0 } : typedRows(r);
    let fallbackRows = false;
    if (typed.count > 0 || typed.count === -1) B += typed.html;
    else if (S.rec || S.occ || S.exp) {
      // The live catalog found nothing for the words, but they named quiz answers: land like taps.
      let R = heroResults();
      if (nm && nm.kind === 'hero')
        R = R.filter(function (x) {
          return x.p[1] !== nm.hero.p[1];
        });
      if (R.length) {
        B += prodRow(R, resHead(), 4, 'results');
        fallbackRows = true;
      }
    }
    const empty =
      typed.count === 0 &&
      !colls.length &&
      !r.help &&
      !r.redeem &&
      !r.place &&
      !nm &&
      !fallbackRows;
    if (empty) {
      B += noResults(false);
      if (S.typed.reported !== S.q) {
        S.typed.reported = S.q;
        track('ew_search_no_results', { item: S.q.trim().slice(0, 100) });
      }
    } else if (typed.count === 0 && r.place) B += mostGifted();
    S.rec = saved.rec;
    S.occ = saved.occ;
    S.exp = saved.exp;
    S.band = saved.band;
    S.part = saved.part;
    return cols(A, B);
  }

  function cols(A, B) {
    return (
      '<div class="sr-col a">' +
      A +
      '</div><div class="sr-col b">' +
      B +
      '</div>'
    );
  }

  function paint(state, quiet) {
    const shell = state.root.closest(
      '[data-search-dropdown], [data-mobile-search-modal]',
    );
    // The container's own `hidden`, not any ancestor's: the desktop field wrapper is
    // `hidden md:block`, so a `.hidden` ancestor does not mean the panel is off screen.
    if (
      !state.root.isConnected ||
      (shell && shell.classList.contains('hidden'))
    )
      return;
    S = state;
    S.date = today();
    const html = S.q.trim()
      ? typedView()
      : S.rec || S.occ || S.exp || S.band || S.door
      ? refineView()
      : emptyView();
    if (quiet && html === state.lastHtml) return;
    state.lastHtml = html;
    const scroller = state.scroller || state.root;
    const top = scroller.scrollTop;
    state.root.innerHTML = html;
    if (state.keepScroll) scroller.scrollTop = top;
    else if (!quiet) scroller.scrollTop = 0;
    state.keepScroll = false;
  }

  function renderAll(quiet) {
    states.forEach(function (st) {
      paint(st, quiet);
    });
  }

  /* ------------------------------------------------------------ typed search against the live catalog */

  function fetchTyped(state) {
    const query = state.q.trim();
    const T = state.typed;
    T.q = query ? state.q : '';
    if (!query) return;
    T.status = 'loading';
    clearTimeout(state.timer);
    state.timer = setTimeout(function () {
      const engine = window.SearchEngine;
      const run =
        engine && engine.isConfigured()
          ? engine.search(query, { pageSize: TYPED_PAGE }).then(function (res) {
              return {
                docs: res.documents,
                total: res.total,
                sprawl: res.sprawl,
                engine: true,
              };
            })
          : Promise.reject(new Error('no engine'));
      run
        .catch(function () {
          return predictive(query);
        })
        .then(function (out) {
          if (state.q !== T.q) return;
          T.docs = out.docs;
          T.total = out.total;
          T.sprawl = out.sprawl;
          T.status = 'done';
          paint(state, true);
          if (out.engine && out.docs.length && engine) {
            engine
              .resolvePrices(out.docs)
              .then(function () {
                if (state.q === T.q) paint(state, true);
              })
              .catch(function () {});
          }
        })
        .catch(function () {
          if (state.q !== T.q) return;
          T.status = 'error';
          T.docs = [];
          paint(state, true);
        });
    }, TYPED_DEBOUNCE);
  }

  function predictive(query) {
    const url =
      routeRoot() +
      'search/suggest.json?q=' +
      encodeURIComponent(query) +
      '&resources[type]=product&resources[limit]=8';
    return fetch(url)
      .then(function (r) {
        return r.json();
      })
      .then(function (data) {
        const products =
          (data.resources &&
            data.resources.results &&
            data.resources.results.products) ||
          [];
        return {
          docs: products.map(function (p) {
            return {
              title: p.title,
              url: p.url,
              handle: p.handle,
              image: p.image || p.featured_image || '',
              basePrice: null,
              priceText: money(parseFloat(p.price_min)),
              variantCount: 0,
              productLayer: '',
            };
          }),
          total: products.length > 4 ? null : products.length,
          sprawl: false,
        };
      });
  }

  /* ------------------------------------------------------------ events */

  function setQuery(state, q) {
    state.input.value = q;
    state.input.dispatchEvent(new Event('input', { bubbles: true }));
  }

  function bandById(id) {
    return D.bud.filter(function (b) {
      return b.id === id;
    })[0];
  }

  function onClick(state, e) {
    const b = e.target.closest(
      '[data-q],[data-door],[data-rec],[data-part],[data-occ],[data-exp],[data-band],[data-amount],[data-drop],[data-more],[data-clear-recent],a',
    );
    if (!b || !state.root.contains(b)) return;
    S = state;
    const has = function (a) {
      return b.hasAttribute(a);
    };
    const chipTrack = function (row, answer) {
      track('ew_search_chip', { level: row, item: answer });
    };
    if (b.tagName === 'A') {
      if (has('data-track-result'))
        track('ew_search_result_click', {
          item: b.getAttribute('data-track-result'),
          place: b.getAttribute('data-track-list'),
          ordinal: Number(b.getAttribute('data-track-pos')) || undefined,
        });
      else if (has('data-track-coll'))
        track('ew_search_result_click', {
          item: b.getAttribute('data-track-coll'),
          place: 'collection',
        });
      if (state.q.trim()) saveRecent(state.q);
      return;
    }
    e.preventDefault();
    if (has('data-q')) {
      const q = b.getAttribute('data-q');
      if (has('data-track-suggestion'))
        track('ew_search_suggestion_click', { item: q });
      saveRecent(q);
      setQuery(state, q);
      state.input.focus({ preventScroll: true });
      return;
    }
    if (has('data-clear-recent')) {
      clearRecent();
      paint(state);
      return;
    }
    const clearTyped = function () {
      if (state.q) {
        state.q = '';
        state.input.value = '';
        state.input.dispatchEvent(new Event('input', { bubbles: true }));
      }
    };
    if (has('data-door')) {
      state.door = b.getAttribute('data-door') || null;
      if (state.door) chipTrack('who', state.door);
      state.keepScroll = true;
      paint(state);
      return;
    }
    if (has('data-rec')) {
      state.rec = b.getAttribute('data-rec');
      state.door = null;
      state.part = null;
      state.more = {};
      if (state.occ && level(D.om, state.occ, state.rec) === '-')
        state.occ = null;
      if (state.exp && level(D.em, state.exp, state.rec) === '-')
        state.exp = null;
      chipTrack('who', state.rec);
      clearTyped();
      paint(state);
      return;
    }
    if (has('data-part')) {
      state.part = b.getAttribute('data-part');
      chipTrack('participation', state.part);
      paint(state);
      return;
    }
    if (has('data-occ')) {
      state.occ = b.getAttribute('data-occ');
      chipTrack('occasion', state.occ);
      clearTyped();
      paint(state);
      return;
    }
    if (has('data-exp')) {
      state.exp = b.getAttribute('data-exp');
      chipTrack('experience', state.exp);
      clearTyped();
      paint(state);
      return;
    }
    if (has('data-band')) {
      state.band = bandById(b.getAttribute('data-band'));
      chipTrack('price', state.band.id);
      clearTyped();
      paint(state);
      return;
    }
    if (has('data-amount')) {
      chipTrack('price', 'amount');
      resetState(state);
      setQuery(state, CFG.amountQuery || 'gift card');
      return;
    }
    if (has('data-more')) {
      state.more[b.getAttribute('data-more')] = true;
      state.keepScroll = true;
      paint(state);
      return;
    }
    if (has('data-drop')) {
      const k = b.getAttribute('data-drop');
      if (k === 'all') resetState(state);
      else if (k === 'rec') {
        state.rec = null;
        state.part = null;
      } else state[k] = null;
      if (state.q) {
        state.q = '';
        state.input.value = '';
        state.input.dispatchEvent(new Event('input', { bubbles: true }));
        return;
      }
      paint(state);
    }
  }

  function resetState(state) {
    state.door = null;
    state.rec = null;
    state.part = null;
    state.occ = null;
    state.exp = null;
    state.band = null;
    state.more = {};
  }

  /* ------------------------------------------------------------ public */

  function mount(root, input, context, scroller) {
    if (states.has(root)) return states.get(root);
    loadConfig();
    const state = {
      root: root,
      input: input,
      context: context,
      scroller: scroller || null,
      q: '',
      door: null,
      rec: null,
      part: null,
      occ: null,
      exp: null,
      band: null,
      more: {},
      date: today(),
      typed: {
        q: '',
        status: 'idle',
        docs: [],
        total: 0,
        sprawl: false,
        reported: null,
      },
      lastHtml: '',
      keepScroll: false,
      timer: null,
    };
    states.set(root, state);
    root.addEventListener('click', function (e) {
      if (D) onClick(state, e);
    });
    return state;
  }

  function show(root, query) {
    const state = states.get(root);
    if (!state) return;
    state.q = query || '';
    loadData()
      .then(function () {
        if (!HIDX || !Object.keys(HIDX).length) prepare();
        if (state.q.trim() && state.typed.q !== state.q) fetchTyped(state);
        paint(state);
      })
      .catch(function (err) {
        console.warn('Search panel unavailable:', err);
      });
  }

  function opened(root, context) {
    track('ew_search_open', { place: context });
    const state = states.get(root);
    if (state) {
      resetState(state);
      state.q = '';
    }
  }

  window.SearchGuided = {
    mount: mount,
    show: show,
    opened: opened,
    saveRecent: saveRecent,
    preload: function () {
      loadConfig();
      return loadData().catch(function () {});
    },
  };
})();
